import {
    BugEntity,
    BugGroupEntity,
    BugNoteEntity,
    MapEntity,
    PhaseEntity,
    ProjectEntity,
    VisitEntity,
    VisitTypeEntity,
    VisitTypeTemplateName,
} from "@archipad-js/archipad-model";
import { IllegalArgumentError, UnsupportedOperationError } from "@archipad-js/core/error";
import { ConfigService } from "@archipad-js/core/config";
import {
    EntityContext,
    generateDeterministicId,
    generateDeterministicTemplateIdFromTemplateIdAndTemplateId,
    query,
} from "@archipad-js/core/orm";
import { LocalizationService } from "@archipad-js/core/translate";

import { BugNoteType } from "./bug-note-type";
import { BugService } from "./bug.service";
import { PhaseService } from "./phase.service";
import { VisitTypeService } from "./visittype.service";

enum BugGroupTemplate {
    defaultGroup = "defaultGroup",
    deliveryGroup = "deliveryGroup",
}

interface IPhaseDescription {
    _originBugGroupTemplateName_: BugGroupTemplate;
    nameSystem: string;
    templateId: string;
}

export interface IPhaseManager {
    migrateIssuesToPhaseCount(visit: VisitEntity, unclassifiedRegionOnly?: boolean): number;

    migrateIssuesToPhase(visit: VisitEntity, unclassifiedRegionOnly?: boolean): void;

    addSystemPhases(entityContext: EntityContext, project: ProjectEntity): Promise<void>;

    findPhaseByNameIdentifier(project: ProjectEntity, phaseNameIdentifier: string): PhaseEntity | null;
    getPhaseNameIdentifier(phase: PhaseEntity): string;
}

/**
 * Creates bug notes for a bug.
 *
 * Decouples {@link PhaseManager} from the legacy `BugNoteFacade`.
 */
export interface BugNoteFactory {
    createBugNote(bug: BugEntity, visit: VisitEntity | null): BugNoteEntity;
}

export class PhaseManager implements IPhaseManager {
    constructor(
        private readonly visitTypeService: VisitTypeService,
        private readonly bugService: BugService,
        private readonly phaseService: PhaseService,
        private readonly bugNoteFactory: BugNoteFactory,
        private readonly configService: ConfigService,
        private readonly localizationService: LocalizationService,
    ) {}

    migrateIssuesToPhaseCount(_visit: VisitEntity, _unclassifiedRegionOnly = false): number {
        throw new UnsupportedOperationError("Method not implemented.");
    }

    migrateIssuesToPhase(_visit: VisitEntity, _unclassifiedRegionOnly = false): void {
        throw new UnsupportedOperationError("Method not implemented.");
    }

    public async getPhasesDescription(): Promise<{ [lang: string]: IPhaseDescription[] } | null> {
        const phases = await this.configService.get("phases");
        return (phases ?? null) as { [lang: string]: IPhaseDescription[] } | null;
    }

    async addSystemPhases(entityContext: EntityContext, project: ProjectEntity): Promise<void> {
        const phasesDescription = await this.getPhasesDescription();
        if (!phasesDescription) {
            // No config loaded with this app
            // NOTE : This avoid ipad-app / report-app to try upgrading phases.
            return;
        }

        // Already have phases, skip adding phase part.
        if (!project.phases.length) {
            const ConfigEntities_phases = phasesDescription[project.language ?? ""] as IPhaseDescription[];

            // NOTE: corresponding orm object is NULL
            const ConfigEntity_bugGroup_default = {
                _templateName_: "defaultGroup",
                templateId: "6485251F3220412089A38D3E28701777",
            };
            const ConfigEntity_bugGroup_delivery = {
                _templateName_: "deliveryGroup",
                templateId: "22976AADBE084EA69650626F98671EFD",
            };

            let phaseIndex = 0;
            for (const systemPhase of ConfigEntities_phases) {
                const templateId = systemPhase.templateId;
                const entityId = generateDeterministicId(project.projectId, templateId, "uuid");
                const bugGroup = systemPhase._originBugGroupTemplateName_;

                const phaseEntity = entityContext.createEntity("Phase", entityId);
                phaseEntity.index = phaseIndex;
                phaseEntity.templateId = templateId;
                phaseEntity.project = project;
                phaseEntity.name = this.localizationService.get(systemPhase.nameSystem).toString();
                phaseEntity.nameSystem = systemPhase.nameSystem;

                if (bugGroup === ConfigEntity_bugGroup_default._templateName_) {
                    const relationTemplateId = generateDeterministicTemplateIdFromTemplateIdAndTemplateId(
                        phaseEntity.templateId,
                        ConfigEntity_bugGroup_default.templateId,
                    );
                    const relationEntityId = generateDeterministicId(project.projectId, relationTemplateId, "uuid");
                    const relationEntity = entityContext.createEntity("Phase_BugGroups", relationEntityId);
                    relationEntity.templateId = relationTemplateId;
                    relationEntity.phase = phaseEntity;
                } else if (bugGroup === ConfigEntity_bugGroup_delivery._templateName_) {
                    const relationTemplateId = generateDeterministicTemplateIdFromTemplateIdAndTemplateId(
                        phaseEntity.templateId,
                        ConfigEntity_bugGroup_delivery.templateId,
                    );
                    const relationEntityId = generateDeterministicId(project.projectId, relationTemplateId, "uuid");

                    const bugGroupQuery = query(entityContext, "BugGroup");
                    bugGroupQuery.predicateWithFormat("templateId == {templateId}", {
                        templateId: ConfigEntity_bugGroup_delivery.templateId,
                    });
                    const resultSet = bugGroupQuery.execute();
                    const bugGroupEntity = resultSet.firstEntity();

                    const relationEntity = entityContext.createEntity("Phase_BugGroups", relationEntityId);
                    relationEntity.templateId = relationTemplateId;
                    relationEntity.phase = phaseEntity;

                    if (bugGroupEntity) {
                        relationEntity.bugGroup = bugGroupEntity;
                    }
                }

                phaseIndex++;
            }
        }

        this.maybeUpdateAfterSalesServiceVisitTypeDefaultPhase(project);

        for (const visitType of project.visitTypes) {
            if (!visitType.phase) {
                visitType.phase = this.phaseService.getDefaultPhaseForBugGroup(project, visitType.bugGroup);
            }
        }

        for (const visit of project.visits) {
            if (!visit.phase && visit.visitType) {
                visit.phase = visit.visitType.phase as PhaseEntity;
            }
        }
    }

    /**
     * Returns the default phase to use for a new bug.
     */
    private getDefaultPhaseForBug(options: {
        project: ProjectEntity;
        referenceBug?: BugEntity | null;
        visitType: VisitTypeEntity | null;
    }): PhaseEntity | null {
        const { project, referenceBug, visitType } = options;

        const isStandardVisit = (visitType?.eventPriority ?? -1) >= 0;
        if (isStandardVisit) {
            return null;
        }

        const defaultPhase = referenceBug?.phase ?? visitType?.phase ?? null;
        const fallbackBugGroup: BugGroupEntity | null = referenceBug?.bugGroup ?? visitType?.bugGroup ?? null;

        if (!defaultPhase && !visitType) {
            return this.phaseService.getDefaultPhaseForBugGroup(project, fallbackBugGroup);
        }
        return defaultPhase;
    }

    phaseForBug(
        project: ProjectEntity,
        map: MapEntity,
        bugGroup: BugGroupEntity,
        visit: VisitEntity,
    ): PhaseEntity | null {
        const referenceBug = this.bugService.findReferenceBug({ map: map, bugGroup: bugGroup ?? null, visit: visit });
        return this.getDefaultPhaseForBug({ project, referenceBug, visitType: visit?.visitType });
    }

    isStandardVisit(visit: VisitEntity): boolean {
        const isStandardVisit = (visit.visitType?.eventPriority ?? -1) >= 0;
        return isStandardVisit;
    }

    /**
     * Find a project phase by name with a given phase identifier.
     *
     * @param phaseNameIdentifier - The phase name identifier (example: "nameSystem:Phase 1" or "name:Phase 1").
     */
    findPhaseByNameIdentifier(project: ProjectEntity, phaseNameIdentifier: string): PhaseEntity | null {
        const isValidPhaseIdentifier =
            phaseNameIdentifier.startsWith("name:") || phaseNameIdentifier.startsWith("nameSystem:");
        if (!isValidPhaseIdentifier) {
            throw new IllegalArgumentError(
                "Invalid phase identifier",
                "phaseNameIdentifier",
                "nameSystem:{phase.nameSystem} | name:{phase.name}",
                phaseNameIdentifier,
            );
        }

        const indexOfColon = phaseNameIdentifier.indexOf(":");
        const propertyName = phaseNameIdentifier.slice(0, indexOfColon) as "nameSystem" | "name";
        const propertyValue = phaseNameIdentifier.slice(indexOfColon + 1) as
            | PhaseEntity["nameSystem"]
            | PhaseEntity["name"];
        const phase = project.phases.find((phase) => phase[propertyName] === propertyValue);
        return phase ?? null;
    }

    findPhaseByTemplateId(entityContext: EntityContext, templateId: string): PhaseEntity | null {
        const phaseQuery = query(entityContext, "Phase");
        phaseQuery.predicateWithFormat("templateId == {templateId}", { templateId });
        const resultSet = phaseQuery.execute();
        const phase = resultSet.firstEntity();
        return phase;
    }

    getPhaseNameIdentifier(phase: PhaseEntity): string {
        return phase.nameSystem ? `nameSystem:${phase.nameSystem}` : `name:${phase.name}`;
    }

    _notCalledFunctionButNecessaryForTranslationToolExtract(): void {
        this.localizationService.l("[phase]:suivi_de_chantier");
        this.localizationService.l("[phase]:pre-cloisons");
        this.localizationService.l("[phase]:cloisons");
        this.localizationService.l("[phase]:pre-livraison");
        this.localizationService.l("[phase]:opr");
        this.localizationService.l("[phase]:reception");
        this.localizationService.l("[phase]:livraison");
        this.localizationService.l("[phase]:30_jours");
        this.localizationService.l("[phase]:gpa");
        this.localizationService.l("[phase]:biennale");
        this.localizationService.l("[phase]:decennale");
        this.localizationService.l("[phase]:pre-construction");
        this.localizationService.l("[phase]:execution");
    }

    autoCreateBugNoteForChangedPhase(project: ProjectEntity): void {
        const entityContext = project.getContext();
        const bugQuery = query(entityContext, "Bug");
        bugQuery.predicateWithFormat("phase != null");
        bugQuery.execute().forEach((bug) => {
            const lastBugNoteConcerningPhase = this.bugService.findLastBugNoteConcerningBugPhase(bug);
            const isConcerningLastPhase = lastBugNoteConcerningPhase && lastBugNoteConcerningPhase.phase === bug.phase;
            if (isConcerningLastPhase) {
                // Ignore if the last changing phase was already noted
                return;
            }
            this.createBugNoteForChangedPhase(bug);
        }, this);
    }

    removeDuplicatedBugNoteHistoryPhases(project: ProjectEntity): void {
        const entityContext = project.getContext();
        for (const bug of project.bugs) {
            const bugNotesOfTypeHistoryPhase = bug.notes
                .filter((note) => note.phase)
                .sort((a, b) => a.creationDate.getTime() - b.creationDate.getTime());
            const notesToRemove: BugNoteEntity[] = [];
            let lastPhase: PhaseEntity | null = null;

            for (const note of bugNotesOfTypeHistoryPhase) {
                const phase = note.phase;

                if (phase === lastPhase && note.type === BugNoteType.TYPE_HISTORY_PHASE) {
                    notesToRemove.push(note);
                } else {
                    lastPhase = phase;
                }
            }

            for (const note of notesToRemove) {
                entityContext.deleteEntity(note);
            }
        }
    }

    createBugNoteForChangedPhase(bug: BugEntity): BugNoteEntity {
        const bugNote = this.bugNoteFactory.createBugNote(bug, null);
        bugNote.type = BugNoteType.TYPE_HISTORY_PHASE;
        bugNote.phase = bug.phase;
        bugNote.creationDate = new Date();
        bugNote.text = this.localizationService.l("Phase changed to {{phase}}", {
            phase: bug.phase?.nameComputed ?? "",
        });
        return bugNote;
    }

    /**
     * @see {@link https://bigsool-archipad.atlassian.net/browse/AP-9218 | AP-9218}
     */
    updateBugsWithUndesiredMissingPhase(project: ProjectEntity): void {
        const bugs = this.bugService.listBugs({ where: { project, phases: [null] } });
        const bugsLinkWithReference = this.bugService.findAllReferenceBugs(project, bugs);
        for (const [bug, referenceBug] of bugsLinkWithReference) {
            const defaultPhase: PhaseEntity | null = this.getDefaultPhaseForBug({
                project,
                referenceBug,
                visitType: bug.visit?.visitType ?? null,
            });
            bug.phase = defaultPhase as PhaseEntity;
        }
    }

    private maybeUpdateAfterSalesServiceVisitTypeDefaultPhase(project: ProjectEntity): void {
        const afterSalesServiceVisitType = this.visitTypeService.findVisitType({
            where: {
                project,
                templateNames: [VisitTypeTemplateName.AfterSalesService],
            },
        });

        if (!afterSalesServiceVisitType) {
            return;
        }

        if (afterSalesServiceVisitType.phase) {
            return;
        }

        const phase30days = this.find30DaysPhase(project);
        afterSalesServiceVisitType.phase = phase30days;
    }

    private find30DaysPhase(project: ProjectEntity): PhaseEntity | null {
        const phase30days = this.findPhaseByNameIdentifier(project, "nameSystem:[phase]:30_jours");
        return phase30days;
    }
}
